import { readModelCatalog, seriesFromModel, seriesFromSelection } from "@theme/vp-model-catalog";
//#region assets/vp-model-select.ts
/**
* Paged Brand → Model → Chassis picker for the kenteken panel.
*
* Expects markup from `snippets/vp-model-select.liquid`.
*/
/** @typedef {'brands' | 'models' | 'chassis' | 'search'} ModelSelectPage */
/**
* @param {string} brandAndModel
* @returns {string}
*/
function chassisCodeFromMetafield(brandAndModel) {
	const match = String(brandAndModel || "").match(/\(([^)]+)\)/);
	return match ? match[1].trim() : "";
}
/**
* @param {string} label
* @param {string} brandAndModel
* @returns {string}
*/
function triggerLabel(label, brandAndModel) {
	const code = chassisCodeFromMetafield(brandAndModel);
	if (!code || !label) return label || code;
	if (String(label).toUpperCase().includes(`(${code.toUpperCase()})`)) return label;
	return `${label} (${code})`;
}
/**
* @param {string} value
* @returns {string}
*/
function escapeRegExp(value) {
	return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
var VpModelSelect = class extends HTMLElement {
	/** @type {HTMLButtonElement | null} */
	#trigger = null;
	/** @type {HTMLInputElement | null} */
	#hiddenInput = null;
	/** @type {HTMLInputElement | null} */
	#brandAndModelInput = null;
	/** @type {HTMLInputElement | null} */
	#searchInput = null;
	/** @type {HTMLElement | null} */
	#list = null;
	/** @type {HTMLElement | null} */
	#empty = null;
	/** @type {HTMLElement | null} */
	#emptyChassis = null;
	/** @type {HTMLElement | null} */
	#label = null;
	/** @type {HTMLElement | null} */
	#labelViewport = null;
	/** @type {ResizeObserver | null} */
	#labelResizeObserver = null;
	/** @type {HTMLButtonElement | null} */
	#clearButton = null;
	/** @type {HTMLElement | null} */
	#nav = null;
	/** @type {HTMLElement | null} */
	#breadcrumb = null;
	/** @type {HTMLElement | null} */
	#brands = null;
	/** @type {HTMLElement | null} */
	#groups = null;
	/** @type {HTMLElement | null} */
	#chassis = null;
	/** @type {HTMLElement | null} */
	#searchResults = null;
	/** @type {HTMLButtonElement | null} */
	#backButton = null;
	/** @type {HTMLButtonElement[]} */
	#modelOptions = [];
	/** @type {import('@theme/vp-model-catalog').VehicleSeries[]} */
	#catalog = [];
	/** @type {number} */
	#activeIndex = -1;
	/** @type {ModelSelectPage} */
	#page = "brands";
	/** @type {ModelSelectPage} */
	#pageBeforeSearch = "brands";
	/** @type {{ brand: string, label: string }} */
	#navState = {
		brand: "",
		label: ""
	};
	/** @type {string} */
	#selectedBrandAndModel = "";
	/** @type {Array<{ type: 'brand'|'model'|'chassis', brand: string, label?: string, brandAndModel?: string, search: string, display: string }>} */
	#searchIndex = [];
	/** @type {(event: MouseEvent) => void} */
	#onDocumentPointerDown = (event) => {
		if (!this.open) return;
		if (event.target instanceof Node && this.contains(event.target)) return;
		this.close();
	};
	connectedCallback() {
		this.#trigger = this.querySelector("[data-vp-model-trigger]");
		this.#hiddenInput = this.querySelector("[data-vp-model-value]");
		this.#brandAndModelInput = this.querySelector("[data-vp-model-brand-and-model]");
		this.#searchInput = this.querySelector("[data-vp-model-search]");
		this.#list = this.querySelector("[data-vp-model-list]");
		this.#empty = this.querySelector("[data-vp-model-empty]");
		this.#emptyChassis = this.querySelector("[data-vp-model-empty-chassis]");
		this.#label = this.querySelector("[data-vp-model-label]");
		this.#labelViewport = this.querySelector("[data-vp-model-label-viewport]");
		this.#clearButton = this.querySelector("[data-vp-model-clear]");
		this.#nav = this.querySelector("[data-vp-model-nav]");
		this.#breadcrumb = this.querySelector("[data-vp-model-breadcrumb]");
		this.#brands = this.querySelector("[data-vp-model-brands]");
		this.#groups = this.querySelector("[data-vp-model-groups]");
		this.#chassis = this.querySelector("[data-vp-model-chassis]");
		this.#searchResults = this.querySelector("[data-vp-model-search-results]");
		this.#backButton = this.querySelector("[data-vp-model-back]");
		this.#modelOptions = Array.from(this.querySelectorAll("[data-vp-model-option]"));
		this.#catalog = readModelCatalog(this);
		if (!this.#trigger || !this.#hiddenInput || !this.#list) return;
		this.#buildSearchIndex();
		this.#trigger.addEventListener("click", () => {
			if (this.open) this.close();
			else this.openPanel();
		});
		this.#trigger.addEventListener("keydown", (event) => this.#onTriggerKeydown(event));
		this.#clearButton?.addEventListener("click", (event) => {
			event.preventDefault();
			event.stopPropagation();
			this.clearSelection();
		});
		this.#backButton?.addEventListener("click", (event) => {
			event.preventDefault();
			this.#goBack();
		});
		if (this.#searchInput) {
			this.#searchInput.addEventListener("input", () => this.#onSearchInput());
			this.#searchInput.addEventListener("keydown", (event) => this.#onSearchKeydown(event));
		}
		this.#list.addEventListener("click", (event) => {
			const target = event.target;
			if (!(target instanceof Element)) return;
			const viewAll = target.closest("[data-vp-model-view-all]");
			if (viewAll instanceof HTMLButtonElement && !viewAll.hidden) {
				this.#selectSeriesAll();
				return;
			}
			const brandButton = target.closest("[data-vp-model-brand]");
			if (brandButton instanceof HTMLButtonElement && !brandButton.hidden) {
				this.#selectBrand(brandButton.dataset.brand || brandButton.textContent?.trim() || "");
				return;
			}
			const modelOption = target.closest("[data-vp-model-option]");
			if (modelOption instanceof HTMLButtonElement && !modelOption.hidden) {
				this.#selectModelOption(modelOption);
				return;
			}
			const chassisOption = target.closest("[data-vp-model-chassis-option]");
			if (chassisOption instanceof HTMLButtonElement && !chassisOption.hidden) {
				this.#selectChassisOption(chassisOption);
				return;
			}
			const searchResult = target.closest("[data-vp-model-search-hit]");
			if (searchResult instanceof HTMLButtonElement && !searchResult.hidden) this.#selectSearchHit(searchResult);
		});
		this.#renderPage();
		this.#syncHasValue();
		if (this.#labelViewport && "ResizeObserver" in window) {
			this.#labelResizeObserver = new ResizeObserver(() => this.#updateMarquee());
			this.#labelResizeObserver.observe(this.#labelViewport);
		}
	}
	disconnectedCallback() {
		document.removeEventListener("pointerdown", this.#onDocumentPointerDown, true);
		this.#labelResizeObserver?.disconnect();
		this.#labelResizeObserver = null;
	}
	get open() {
		return this.hasAttribute("open");
	}
	openPanel() {
		if (this.open) return;
		this.setAttribute("open", "");
		this.#trigger?.setAttribute("aria-expanded", "true");
		document.addEventListener("pointerdown", this.#onDocumentPointerDown, true);
		this.#page = "brands";
		this.#navState = {
			brand: "",
			label: ""
		};
		this.#pageBeforeSearch = "brands";
		if (this.#searchInput) this.#searchInput.value = "";
		this.#renderPage();
		if (this.#searchInput) requestAnimationFrame(() => this.#searchInput?.focus());
		else this.#setActiveIndex(this.#visibleOptions().findIndex((option) => option.getAttribute("aria-selected") === "true"));
	}
	close({ restoreFocus = true } = {}) {
		if (!this.open) return;
		this.removeAttribute("open");
		this.#trigger?.setAttribute("aria-expanded", "false");
		document.removeEventListener("pointerdown", this.#onDocumentPointerDown, true);
		this.#clearActive();
		if (restoreFocus) this.#trigger?.focus();
	}
	/**
	* @param {KeyboardEvent} event
	*/
	#onTriggerKeydown(event) {
		if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			this.openPanel();
		} else if (event.key === "Escape" && this.open) {
			event.preventDefault();
			this.close();
		}
	}
	/**
	* @param {KeyboardEvent} event
	*/
	#onSearchKeydown(event) {
		const visible = this.#visibleOptions();
		if (event.key === "Escape") {
			event.preventDefault();
			this.close();
			return;
		}
		if (event.key === "ArrowDown") {
			event.preventDefault();
			this.#moveActive(1, visible);
			return;
		}
		if (event.key === "ArrowUp") {
			event.preventDefault();
			this.#moveActive(-1, visible);
			return;
		}
		if (event.key === "Enter") {
			event.preventDefault();
			const option = visible[this.#activeIndex] || visible[0];
			if (!option) return;
			if (option.matches("[data-vp-model-brand]")) this.#selectBrand(option.dataset.brand || "");
			else if (option.matches("[data-vp-model-option]")) this.#selectModelOption(option);
			else if (option.matches("[data-vp-model-chassis-option]")) this.#selectChassisOption(option);
			else if (option.matches("[data-vp-model-search-hit]")) this.#selectSearchHit(option);
		}
	}
	#onSearchInput() {
		const query = this.#searchInput?.value || "";
		if (!query.trim()) {
			this.#page = this.#pageBeforeSearch;
			this.#renderPage();
			return;
		}
		if (this.#page !== "search") {
			this.#pageBeforeSearch = this.#page;
			this.#page = "search";
		}
		this.#renderSearch(query);
	}
	#buildSearchIndex() {
		/** @type {typeof this.#searchIndex} */
		const index = [];
		index.push({
			type: "brand",
			brand: "BMW",
			search: "bmw",
			display: "BMW"
		});
		index.push({
			type: "brand",
			brand: "MINI",
			search: "mini",
			display: "MINI"
		});
		for (const entry of this.#catalog) {
			const { brand, label } = entry;
			index.push({
				type: "model",
				brand,
				label,
				search: `${brand} ${label}`.toLowerCase(),
				display: `${brand} ${label}`
			});
			for (const brandAndModel of entry.models) {
				const code = chassisCodeFromMetafield(brandAndModel);
				const yearRange = String(entry.yearRanges?.[brandAndModel] || "").trim();
				index.push({
					type: "chassis",
					brand,
					label,
					brandAndModel,
					search: `${brand} ${label} ${brandAndModel} ${code} ${yearRange}`.toLowerCase(),
					display: yearRange ? `${brand} ${label} (${code}) ${yearRange}` : `${brand} ${label} (${code})`
				});
			}
		}
		this.#searchIndex = index;
	}
	/**
	* @param {string} query
	*/
	#renderSearch(query) {
		const normalized = query.trim().toLowerCase();
		const hits = this.#searchIndex.filter((item) => item.search.includes(normalized));
		if (!this.#searchResults) return;
		this.#brands?.setAttribute("hidden", "");
		this.#groups?.setAttribute("hidden", "");
		this.#chassis?.setAttribute("hidden", "");
		this.#nav?.setAttribute("hidden", "");
		this.#searchResults.hidden = false;
		this.#searchResults.removeAttribute("hidden");
		this.#searchResults.innerHTML = "";
		for (const hit of hits) {
			const button = document.createElement("button");
			button.type = "button";
			button.className = "vp-model-select__option";
			button.role = "option";
			button.dataset.vpModelSearchHit = "";
			button.dataset.hitType = hit.type;
			button.dataset.brand = hit.brand;
			if (hit.label) button.dataset.label = hit.label;
			if (hit.brandAndModel) button.dataset.brandAndModel = hit.brandAndModel;
			button.textContent = hit.display;
			this.#searchResults.appendChild(button);
		}
		this.#updateEmptyStates(hits.length === 0, false);
		this.#setActiveIndex(hits.length > 0 ? 0 : -1);
	}
	#renderPage() {
		if (this.#page === "search") return;
		this.#searchResults?.setAttribute("hidden", "");
		if (this.#searchResults) this.#searchResults.hidden = true;
		this.#emptyChassis?.removeAttribute("data-visible");
		if (this.#emptyChassis) this.#emptyChassis.hidden = true;
		if (this.#page === "brands") {
			this.#brands?.removeAttribute("hidden");
			this.#groups?.setAttribute("hidden", "");
			this.#chassis?.setAttribute("hidden", "");
			this.#nav?.setAttribute("hidden", "");
			this.#updateEmptyStates(false, false);
		} else if (this.#page === "models") {
			this.#brands?.setAttribute("hidden", "");
			this.#groups?.removeAttribute("hidden");
			this.#chassis?.setAttribute("hidden", "");
			this.#nav?.removeAttribute("hidden");
			this.querySelectorAll("[data-vp-model-group]").forEach((group) => {
				if (!(group instanceof HTMLElement)) return;
				group.hidden = (group.getAttribute("data-brand") || "") !== this.#navState.brand;
			});
			if (this.#breadcrumb) this.#breadcrumb.textContent = this.#navState.brand;
			this.#updateEmptyStates(false, false);
		} else if (this.#page === "chassis") {
			this.#brands?.setAttribute("hidden", "");
			this.#groups?.setAttribute("hidden", "");
			this.#chassis?.removeAttribute("hidden");
			this.#nav?.removeAttribute("hidden");
			if (this.#breadcrumb) this.#breadcrumb.textContent = `${this.#navState.brand} › ${this.#navState.label}`;
			this.#renderChassisOptions();
		}
		this.#setActiveIndex(this.#visibleOptions().length > 0 ? 0 : -1);
	}
	#renderChassisOptions() {
		if (!this.#chassis) return;
		const models = seriesFromSelection(this.#catalog, this.#navState.brand, this.#navState.label)?.models || [];
		this.#chassis.innerHTML = "";
		if (!models.length) {
			this.#updateEmptyStates(false, true);
			return;
		}
		this.#updateEmptyStates(false, false);
		const yearRanges = seriesFromSelection(this.#catalog, this.#navState.brand, this.#navState.label)?.yearRanges || {};
		for (const brandAndModel of models) {
			const code = chassisCodeFromMetafield(brandAndModel);
			const yearRange = String(yearRanges[brandAndModel] || "").trim();
			const button = document.createElement("button");
			button.type = "button";
			button.className = "vp-model-select__option";
			button.role = "option";
			button.dataset.vpModelChassisOption = "";
			button.dataset.brandAndModel = brandAndModel;
			button.dataset.brand = this.#navState.brand;
			button.dataset.label = this.#navState.label;
			button.dataset.search = `${this.#navState.brand} ${this.#navState.label} ${brandAndModel} ${code} ${yearRange}`.toLowerCase();
			button.textContent = yearRange ? `${code} (${yearRange})` : code;
			button.setAttribute("aria-selected", this.#selectedBrandAndModel === brandAndModel ? "true" : "false");
			this.#chassis.appendChild(button);
		}
		if (models.length > 1) {
			const viewAll = document.createElement("button");
			viewAll.type = "button";
			viewAll.className = "vp-model-select__view-all";
			viewAll.dataset.vpModelViewAll = "";
			viewAll.textContent = "Bekijk alle accessoires";
			this.#chassis.appendChild(viewAll);
		}
	}
	/**
	* @param {boolean} showSearchEmpty
	* @param {boolean} showChassisEmpty
	*/
	#updateEmptyStates(showSearchEmpty, showChassisEmpty) {
		if (this.#empty) {
			if (showSearchEmpty) this.#empty.setAttribute("data-visible", "");
			else this.#empty.removeAttribute("data-visible");
		}
		if (this.#emptyChassis) {
			if (showChassisEmpty) {
				this.#emptyChassis.removeAttribute("hidden");
				this.#emptyChassis.setAttribute("data-visible", "");
			} else {
				this.#emptyChassis.hidden = true;
				this.#emptyChassis.removeAttribute("data-visible");
			}
		}
	}
	/**
	* @param {string} brand
	*/
	#selectBrand(brand) {
		if (!brand) return;
		this.#navState = {
			brand,
			label: ""
		};
		this.#page = "models";
		this.#pageBeforeSearch = "models";
		this.#renderPage();
	}
	/**
	* @param {HTMLButtonElement} option
	*/
	#selectModelOption(option) {
		const brand = option.closest("[data-vp-model-group]")?.getAttribute("data-brand") || this.#navState.brand || "";
		const label = option.dataset.label || option.dataset.value || option.textContent?.trim() || "";
		if (!brand || !label) return;
		this.#navState = {
			brand,
			label
		};
		this.#handleModelChoice(brand, label);
	}
	/**
	* @param {string} brand
	* @param {string} label
	*/
	#handleModelChoice(brand, label) {
		const models = seriesFromSelection(this.#catalog, brand, label)?.models || [];
		if (models.length === 1) {
			this.#applySelection({
				brand,
				label,
				brandAndModel: models[0]
			});
			return;
		}
		if (models.length === 0) {
			this.#page = "chassis";
			this.#pageBeforeSearch = "chassis";
			this.#renderPage();
			return;
		}
		this.#page = "chassis";
		this.#pageBeforeSearch = "chassis";
		this.#renderPage();
	}
	/**
	* @param {HTMLButtonElement} option
	*/
	#selectChassisOption(option) {
		const brand = option.dataset.brand || this.#navState.brand;
		const label = option.dataset.label || this.#navState.label;
		const brandAndModel = option.dataset.brandAndModel || "";
		if (!brand || !label || !brandAndModel) return;
		this.#applySelection({
			brand,
			label,
			brandAndModel
		});
	}
	/**
	* Series-level pick when the shopper does not know the chassis code.
	* Filters every generation in the series (e.g. all BMW 1 Serie products).
	*/
	#selectSeriesAll() {
		const brand = this.#navState.brand;
		const label = this.#navState.label;
		const brandAndModels = seriesFromSelection(this.#catalog, brand, label)?.models || [];
		if (!brand || !label || brandAndModels.length === 0) return;
		this.#applySeriesSelection({
			brand,
			label,
			brandAndModels
		});
	}
	/**
	* @param {HTMLButtonElement} button
	*/
	#selectSearchHit(button) {
		const type = button.dataset.hitType || "";
		const brand = button.dataset.brand || "";
		const label = button.dataset.label || "";
		const brandAndModel = button.dataset.brandAndModel || "";
		if (type === "brand") {
			this.#selectBrand(brand);
			return;
		}
		if (type === "chassis" && brandAndModel) {
			this.#applySelection({
				brand,
				label,
				brandAndModel
			});
			return;
		}
		if (type === "model" && brand && label) {
			this.#navState = {
				brand,
				label
			};
			this.#handleModelChoice(brand, label);
		}
	}
	#goBack() {
		if (this.#searchInput?.value.trim()) {
			this.#searchInput.value = "";
			this.#page = this.#pageBeforeSearch;
			this.#renderPage();
			return;
		}
		if (this.#page === "search") {
			this.#page = this.#pageBeforeSearch;
			this.#renderPage();
			return;
		}
		if (this.#page === "chassis") {
			this.#page = "models";
			this.#navState.label = "";
			this.#pageBeforeSearch = "models";
			this.#renderPage();
			return;
		}
		if (this.#page === "models") {
			this.#page = "brands";
			this.#navState = {
				brand: "",
				label: ""
			};
			this.#pageBeforeSearch = "brands";
			this.#renderPage();
		}
	}
	/**
	* @param {{ brand: string, label: string, brandAndModel: string, dispatch?: boolean }} selection
	*/
	#applySelection({ brand, label, brandAndModel, dispatch = true }) {
		this.#selectedBrandAndModel = brandAndModel;
		this.#navState = {
			brand,
			label
		};
		this.#modelOptions.forEach((item) => {
			const itemBrand = item.closest("[data-vp-model-group]")?.getAttribute("data-brand") || "";
			const itemLabel = item.dataset.label || item.dataset.value || "";
			const selected = itemBrand === brand && itemLabel === label;
			item.setAttribute("aria-selected", selected ? "true" : "false");
		});
		if (this.#hiddenInput) this.#hiddenInput.value = label;
		if (this.#brandAndModelInput) this.#brandAndModelInput.value = brandAndModel;
		if (this.#label) this.#label.textContent = triggerLabel(label, brandAndModel);
		this.#syncHasValue();
		this.close();
		if (dispatch) this.dispatchEvent(new CustomEvent("vp-model-select:change", {
			bubbles: true,
			detail: {
				value: label,
				label,
				brand,
				brandAndModel,
				brandAndModels: brandAndModel ? [brandAndModel] : [],
				viewAll: false
			}
		}));
	}
	/**
	* @param {{ brand: string, label: string, brandAndModels: string[], dispatch?: boolean }} selection
	*/
	#applySeriesSelection({ brand, label, brandAndModels, dispatch = true }) {
		this.#selectedBrandAndModel = "";
		this.#navState = {
			brand,
			label
		};
		this.#modelOptions.forEach((item) => {
			const itemBrand = item.closest("[data-vp-model-group]")?.getAttribute("data-brand") || "";
			const itemLabel = item.dataset.label || item.dataset.value || "";
			const selected = itemBrand === brand && itemLabel === label;
			item.setAttribute("aria-selected", selected ? "true" : "false");
		});
		if (this.#hiddenInput) this.#hiddenInput.value = label;
		if (this.#brandAndModelInput) this.#brandAndModelInput.value = "";
		if (this.#label) {
			const alreadyHasBrand = brand && new RegExp(`^${escapeRegExp(brand)}\\b`, "i").test(label);
			this.#label.textContent = brand && !alreadyHasBrand ? `${brand} ${label}` : label;
		}
		this.#syncHasValue();
		this.close();
		if (dispatch) this.dispatchEvent(new CustomEvent("vp-model-select:change", {
			bubbles: true,
			detail: {
				value: label,
				label,
				brand,
				brandAndModel: "",
				brandAndModels,
				viewAll: true
			}
		}));
	}
	/**
	* @returns {HTMLButtonElement[]}
	*/
	#visibleOptions() {
		if (this.#page === "search" && this.#searchResults) return Array.from(this.#searchResults.querySelectorAll("[data-vp-model-search-hit]")).filter((option) => option instanceof HTMLButtonElement && !option.hidden);
		/** @type {HTMLButtonElement[]} */
		const options = [];
		if (this.#page === "brands" && this.#brands) options.push(...Array.from(this.#brands.querySelectorAll("[data-vp-model-brand]")).filter((el) => el instanceof HTMLButtonElement && !el.hidden));
		if (this.#page === "models") options.push(...this.#modelOptions.filter((option) => {
			const group = option.closest("[data-vp-model-group]");
			if (!(group instanceof HTMLElement) || group.hidden) return false;
			return !option.hidden;
		}));
		if (this.#page === "chassis" && this.#chassis) options.push(...Array.from(this.#chassis.querySelectorAll("[data-vp-model-chassis-option]")).filter((el) => el instanceof HTMLButtonElement && !el.hidden));
		return options;
	}
	/**
	* @param {number} delta
	* @param {HTMLButtonElement[]} visible
	*/
	#moveActive(delta, visible = this.#visibleOptions()) {
		if (!visible.length) {
			this.#setActiveIndex(-1);
			return;
		}
		let next = this.#activeIndex + delta;
		if (this.#activeIndex < 0) next = delta > 0 ? 0 : visible.length - 1;
		if (next < 0) next = visible.length - 1;
		if (next >= visible.length) next = 0;
		this.#setActiveIndex(next, visible);
	}
	/**
	* @param {number} index
	* @param {HTMLButtonElement[]} [visible]
	*/
	#setActiveIndex(index, visible = this.#visibleOptions()) {
		this.#clearActive();
		this.#activeIndex = index;
		const option = visible[index];
		if (!option) return;
		option.classList.add("is-active");
		option.scrollIntoView({ block: "nearest" });
	}
	#clearActive() {
		this.querySelectorAll(".is-active").forEach((option) => option.classList.remove("is-active"));
		this.#activeIndex = -1;
	}
	/**
	* Programmatically select a model (e.g. after kenteken lookup).
	*
	* @param {{ value?: string, label?: string, brand?: string, brandAndModel?: string, silent?: boolean }} selection
	* @returns {boolean}
	*/
	setSelection({ value = "", label = "", brand = "", brandAndModel = "", silent = false } = {}) {
		const catalogSeries = brandAndModel ? seriesFromModel(this.#catalog, brandAndModel) : null;
		const resolvedBrand = catalogSeries?.brand || brand || "";
		const resolvedLabel = catalogSeries?.label || label || value;
		if (brandAndModel && resolvedBrand && resolvedLabel) {
			this.#applySelection({
				brand: resolvedBrand,
				label: resolvedLabel,
				brandAndModel,
				dispatch: !silent
			});
			return true;
		}
		const option = this.#findOption(value, resolvedLabel, resolvedBrand);
		if (option) {
			const optionBrand = option.closest("[data-vp-model-group]")?.getAttribute("data-brand") || resolvedBrand;
			const optionLabel = option.dataset.label || option.dataset.value || resolvedLabel;
			const models = seriesFromSelection(this.#catalog, optionBrand, optionLabel)?.models || [];
			if (models.length === 1) {
				this.#applySelection({
					brand: optionBrand,
					label: optionLabel,
					brandAndModel: models[0],
					dispatch: !silent
				});
				return true;
			}
		}
		const display = resolvedLabel || value;
		if (!display) return false;
		this.#modelOptions.forEach((item) => item.setAttribute("aria-selected", "false"));
		if (this.#hiddenInput) this.#hiddenInput.value = value || resolvedLabel;
		if (this.#brandAndModelInput) this.#brandAndModelInput.value = "";
		this.#selectedBrandAndModel = "";
		if (this.#label) {
			const alreadyHasBrand = resolvedBrand && new RegExp(`^${escapeRegExp(resolvedBrand)}\\b`, "i").test(display);
			this.#label.textContent = resolvedBrand && !alreadyHasBrand ? `${resolvedBrand} ${display}` : display;
		}
		this.#syncHasValue();
		return true;
	}
	clearSelection({ silent = false } = {}) {
		this.#modelOptions.forEach((item) => item.setAttribute("aria-selected", "false"));
		if (this.#hiddenInput) this.#hiddenInput.value = "";
		if (this.#brandAndModelInput) this.#brandAndModelInput.value = "";
		this.#selectedBrandAndModel = "";
		if (this.#label) {
			const fallback = this.querySelector("label")?.textContent?.trim() || "Selecteer model";
			this.#label.textContent = fallback;
		}
		this.#syncHasValue();
		if (!silent) this.dispatchEvent(new CustomEvent("vp-model-select:change", {
			bubbles: true,
			detail: {
				value: "",
				label: "",
				brand: "",
				brandAndModel: "",
				brandAndModels: [],
				viewAll: false
			}
		}));
	}
	/**
	* @param {string} value
	* @param {string} label
	* @param {string} brand
	* @returns {HTMLButtonElement|null}
	*/
	#findOption(value, label, brand) {
		const normalizedValue = String(value || "").trim().toLowerCase();
		const normalizedLabel = String(label || "").trim().toLowerCase();
		const normalizedBrand = String(brand || "").trim().toLowerCase();
		return this.#modelOptions.find((option) => {
			const optionBrand = option.closest("[data-vp-model-group]")?.getAttribute("data-brand") || "";
			if (normalizedBrand && optionBrand.toLowerCase() !== normalizedBrand) return false;
			const optionValue = (option.dataset.value || "").trim().toLowerCase();
			const optionLabel = (option.dataset.label || option.textContent || "").trim().toLowerCase();
			if (normalizedValue && optionValue === normalizedValue) return true;
			if (normalizedLabel && optionLabel === normalizedLabel) return true;
			return false;
		}) || null;
	}
	#syncHasValue() {
		const hasValue = Boolean(this.#hiddenInput?.value);
		if (hasValue) this.setAttribute("data-has-value", "");
		else this.removeAttribute("data-has-value");
		if (this.#clearButton) this.#clearButton.hidden = !hasValue;
		this.#updateMarquee();
	}
	/**
	* Trigger keeps a fixed width, so a label that no longer fits scrolls
	* back and forth instead of stretching the control.
	*/
	#updateMarquee() {
		const viewport = this.#labelViewport;
		const text = this.#label;
		if (!viewport || !text) return;
		viewport.removeAttribute("data-marquee");
		viewport.style.removeProperty("--vp-model-select-marquee-shift");
		viewport.style.removeProperty("--vp-model-select-marquee-duration");
		requestAnimationFrame(() => {
			const available = viewport.clientWidth;
			if (available === 0) return;
			const overflow = text.scrollWidth - available;
			if (overflow <= 1) return;
			viewport.style.setProperty("--vp-model-select-marquee-shift", `${-overflow}px`);
			const duration = Math.min(12, Math.max(3.5, overflow / 28));
			viewport.style.setProperty("--vp-model-select-marquee-duration", `${duration.toFixed(2)}s`);
			viewport.setAttribute("data-marquee", "");
		});
	}
};
if (!customElements.get("vp-model-select")) customElements.define("vp-model-select", VpModelSelect);
//#endregion
